import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { isEmpty } from 'lodash';
import isEqual from 'lodash/isEqual';

import { useInjection } from '@context/inversify-context-provider';
import { useOverlayLoader } from '@context/OverlayLoaderContext';
import { hasField, isDesktop } from '@helpers/isDesktop';
import { useGetOnboardingsWithRelationsMultiple } from '@hooks/apiHooks';
import { Tenant } from '@hooks/useComponentMapping';
import { TYPES } from '@libs/InversifyType';
import { Box, Button, Grid } from '@mui/material';
import { RootState } from '@store/rootReducer';
import { setUnsavedChangesExist } from '@store/slices/oneOnboarding.slice';
import { snackbarSlice } from '@store/slices/snackbar';
import { useQueryClient } from '@tanstack/react-query';

import BottomBox from '../../../components/shared/BottomBox/BottomBox';
import useBackendTranslations from '../../../hooks/useBackendTranslations';
import { AamBackendApi } from '../../../libs/aamBackendApi';
import { OONavigationContext } from '../context/NavigationContext';
import { editableCondition, mandatoryCondition, renderCondition } from '../hooks/useRenderCondition';
import { useT } from '../hooks/useT';
import { Component, OOSteps } from '../interfaces';
import { OOControlModel } from '../models/ControlModel';
import { OOPageModel } from '../models/FormModel';
import ErrorPageDialog from '../pages/ErrorPage/ErrorPageDialog';
import SuccessPageDialog from '../pages/SuccessDialog/SuccessPageDialog';
import { OOFlowWrapper } from '../wrappers/FlowWrapper';

import { OOFormControl } from './FormControl';

interface OOFormProps {
  form: OOPageModel;
  flowWrapper: OOFlowWrapper;
  forwardOnSubmit?: boolean;
  step?: OOSteps;
  fromSelfService?: boolean;
}

export interface SdkCapturedDocumentType {
  images: string[];
  meta?: any[];
  transactionId?: string;
}

export const OOForm: React.FC<OOFormProps> = ({
  form,
  flowWrapper,
  forwardOnSubmit,
  step,
  fromSelfService,
}: OOFormProps) => {
  const { t, i18n } = useT('entry', 'documents', 'approval', 'hiring', 'candidate_recruiter');
  const navigationContext = React.useContext(OONavigationContext);
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const store = useStore<RootState>();
  const { controls } = form;
  const [changeCounter, setChangeCounter] = useState<number>(0);
  const [clickCounter, setClickCounter] = useState<number>(0);
  const [manualErrors, setManualErrors] = useState<Map<string, any>>(new Map());
  const [saveEnabled, setSaveEnabled] = useState(true);
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useOverlayLoader();
  const [sdkSuccess, setSdkSuccess] = useState(false);
  const [sdkError, setSdkError] = useState(false);
  const [sdkCapturedDocument, setSdkCapturedDocument] = useState<SdkCapturedDocumentType | null>(null);

  const dispatch = useDispatch();
  const { applicationId } = useSelector((state: RootState) => state.oneOnboarding);
  const { userId } = useSelector((state: RootState) => state.authApp);

  useGetOnboardingsWithRelationsMultiple(
    store.getState().oneOnboarding.clientOrganizationId!,
    store.getState().oneOnboarding.selectedConfigurationId!,
    store.getState().authApp.userId!,
    store.getState().oneOnboarding.applicationId!,
  );

  useBackendTranslations(i18n, fromSelfService);

  const formikRef = useRef<any>(null);

  const formHasCustomSubmitButtons = controls.some(
    (c) => c.isVisibleCandidate && !c.component?.toLowerCase().includes('submit'),
  );

  const [dynamicValidationSchema, setDynamicValidationSchema] = useState(form.getValidationSchema(flowWrapper, t));

  useEffect(() => {
    store.dispatch(setUnsavedChangesExist(!isEqual(formikRef.current?.values, formikRef.current?.initialValues)));
    setDynamicValidationSchema(
      form.getValidationSchema(flowWrapper, t, formikRef.current?.values, undefined, manualErrors),
    );
    if (formikRef.current) {
      formikRef.current.validateForm(formikRef.current?.values);
    }
  }, [changeCounter, flowWrapper, form, store, t, manualErrors]);

  const controlIsStatic = (control: any) =>
    [Component.StaticText, Component.Accordion, Component.Image].includes(control.component) ||
    !control.isEditableCandidate;

  const formIsStatic = controls.every((control) => controlIsStatic(control));

  const getPrefix = (label: string) => {
    let prefix = label?.split('.')?.[0].toLowerCase() || '';
    const prefixAlias: any = {
      documents2: 'documents',
      documents3: 'documents',
    };
    if (prefixAlias[prefix]) {
      prefix = prefixAlias[prefix];
    }
    return prefix;
  };

  const handleAzureAddressValidation = async (
    data: Record<string, any>,
    actions: FormikHelpers<Record<string, any>>,
  ) => {
    try {
      const { STREET: street, TOWN: town, STATE: state, POST_CODE: postCode, SAME_POSTAL_ADDRESS: samePostalAddress } =
        data || {};
      const addressConcat = `${street}, ${town} ${state} ${postCode}`;
      const addressPromises = [
        aamBackendApi.azureAddressValidation(addressConcat, Tenant.Australia, store.getState().authApp.userId!, true),
      ];
      if (!samePostalAddress) {
        const {
          POSTAL_STREET: postalStreet,
          POSTAL_TOWN: postalTown,
          POSTAL_STATE: postalState,
          POSTAL_POST_CODE: postalPostCode,
        } = data;
        const postAddressConcat = `${postalStreet}, ${postalTown} ${postalState} ${postalPostCode}`;
        addressPromises.push(
          aamBackendApi.azureAddressValidation(
            postAddressConcat,
            Tenant.Australia,
            store.getState().authApp.userId!,
            false,
          ),
        );
      }
      const results = await Promise.all(addressPromises);

      await aamBackendApi.fetchUserFlowWithCurrentStepInfoAndUpdateRedux(store.getState().authApp.userId!);

      if (results.some((obj: any) => obj.results.length === 0)) return;

      return results;
    } catch (err) {
      actions.setSubmitting(false);
      return;
    }
  };

  const submitHandler = async (values: Record<string, any>, actions: FormikHelpers<Record<string, any>>) => {
    const payloadData = flowWrapper.generateArrayValues(values);
    ['DEVICE_CHECK_QR', 'DEVICE_CHECK_TITLE'].forEach((k) => delete payloadData[k]);

    const data = {
      data: payloadData,
      currentStep: step || store.getState().oneOnboarding.step,
      currentPage: form.name,
      skipForward: forwardOnSubmit === false || values['SKIP_FORWARD'] === true,
    };
    showLoader(t('candidate_recruiter:GENERAL.GENERIC.Submitting'));
    if (flowWrapper.isAddressValidator() && !data.data.AZURE_ADDRESS_VALIDATION_SKIP) {
      const results = await handleAzureAddressValidation(data.data, actions);

      if (!results) {
        hideLoader();
        return;
      }
    }

    OOFlowWrapper.prepareValues(controls, data.data);
    // errorsOnSave added for error messages to be displayed, because when we have error in array co ntrol it is not displayed on save
    let errorsOnSave;
    try {
      await aamBackendApi.updateUserFlow(
        store.getState().oneOnboarding.clientOrganizationId!,
        store.getState().oneOnboarding.selectedConfigurationId!,
        store.getState().authApp.userId!,
        data,
      );
      await queryClient.invalidateQueries({ queryKey: ['getFlowAndUpdateRedux', store.getState().authApp.userId!] });

      if (forwardOnSubmit !== false && values['SKIP_FORWARD'] !== true) {
        navigationContext.forward();
      }
    } catch (err) {
      if (err?.response?.data?.error?.data?.message) {
        const errors = JSON.parse(err.response.data.error.data.message);

        if (Object.keys(errors).length > 0) {
          const controlStatus: Record<string, any> = {};
          Object.keys(errors).forEach((x) => {
            const control = controls.find((c) => c.name === x);
            if (errors[x].message === 'required' && control?.mandatoryValidationError) {
              controlStatus[x] = t(control.mandatoryValidationError);
              return;
            }
            const prefix = getPrefix(errors[x].message);
            controlStatus[x] = t(`${prefix}:${errors[x].message}`) || '';
          });
          actions.setErrors(controlStatus);
          errorsOnSave = controlStatus;
        }
      }
    } finally {
      actions.setSubmitting(false);
      hideLoader();
      // 'SKIP_FORWARD' is set as a form value, but must be one-time exception (one submission)
      // so we remove it to unlock the form for the progress on the next submission
      if (values['SKIP_FORWARD'] && !errorsOnSave) {
        formikRef.current.setFieldValue('SKIP_FORWARD', undefined);
      }
    }
  };

  const dismissHandler = async (
    setErrors: (errors: FormikErrors<Record<string, any>>) => void,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    setErrors({});

    const data = {
      data: {},
      currentStep: step || store.getState().oneOnboarding.step,
      currentPage: form.name,
      isDismiss: true,
    };
    try {
      await aamBackendApi.updateUserFlow(
        store.getState().oneOnboarding.clientOrganizationId!,
        store.getState().oneOnboarding.selectedConfigurationId!,
        store.getState().authApp.userId!,
        data,
      );
      if (forwardOnSubmit !== false) {
        navigationContext.forward();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isCandidateControl = (control: OOControlModel) => {
    return (
      control.isVisibleCandidate &&
      !control.component?.toLowerCase().includes('submit') &&
      !control.component?.includes(Component.DismissButton)
    );
  };

  const isCandidateSubmitOrDismissBtn = (control: OOControlModel) => {
    return (
      control.isVisibleCandidate &&
      (control.component?.toLowerCase().includes('submit') ||
        (forwardOnSubmit !== false && control.component?.includes(Component.DismissButton)))
    );
  };

  const isCandidateDismissBtn = (control: OOControlModel) => {
    return (
      forwardOnSubmit !== false && control.isVisibleCandidate && control.component?.includes(Component.DismissButton)
    );
  };

  const onFormChange = () => {
    setChangeCounter((prevValue: number) => prevValue + 1);
  };

  const initialValues = useMemo(() => {
    return form.getInitialValues(flowWrapper);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowWrapper]);

  useEffect(() => {
    const el = document.querySelector('.MuiFormHelperText-root.Mui-error');
    (el?.parentElement ?? el)?.scrollIntoView(true);
  }, [clickCounter]);

  const isControlEditable = (isEditableCandidate: boolean, isEditableRecruiter: boolean): boolean => {
    return OOFlowWrapper.isRecruiter() ? isEditableRecruiter : isEditableCandidate;
  };

  const isSaveEnabled = () => {
    const values = formikRef?.current?.values;
    if (!values) return;
    const controlsFiltered = controls
      .filter((c) => c.isVisibleRecruiter && renderCondition(c, flowWrapper, values))
      .map((control: OOControlModel) => {
        control.isMandatory = mandatoryCondition(control, flowWrapper, values) ?? control.isMandatory;
        flowWrapper.getDisabledFieldForDE(control, flowWrapper);
        return control;
      });

    const isEditable = controlsFiltered.some((control) => {
      const editableResult = isControlEditable(control.isEditableCandidate, control.isEditableRecruiter) ?? true;
      const editableConditionResult = editableCondition(control, flowWrapper, values);
      return editableResult || editableConditionResult;
    });

    const jumpCondition = form?.jumpCondition;
    const navigationPage = navigationContext.currentPage;
    const currentPage = store.getState().oneOnboarding.currentForm;

    if (!isEditable && jumpCondition && navigationPage !== currentPage) setSaveEnabled(false);
    const deviceConsentPresent = hasField(values, 'DEVICE_CHECK_CONSENT');
    if (deviceConsentPresent && (Boolean(values?.DEVICE_CHECK_CONSENT) || isDesktop())) {
      setSaveEnabled(false);
    } else {
      setSaveEnabled(true);
    }
  };

  const handleDCDialogSubmit = async () => {
    try {
      const data = {
        data: {},
        currentStep: step || store.getState().oneOnboarding.step,
        currentPage: form.name,
        skipForward: false,
      };

      const updateUserFlowPromise = aamBackendApi.updateUserFlow(
        store.getState().oneOnboarding.clientOrganizationId!,
        store.getState().oneOnboarding.selectedConfigurationId!,
        store.getState().authApp.userId!,
        data,
      );

      const invalidateQueriesPromise = queryClient.invalidateQueries({
        queryKey: ['getFlowAndUpdateRedux', store.getState().authApp.userId!],
      });

      const transactionId = sdkCapturedDocument?.transactionId;
      const images = sdkCapturedDocument?.images;

      const payload = {
        images,
        transactionId,
      };

      const updateFaceVerifyPromise = aamBackendApi.updateFaceVerifyDocumentWithDC(userId, applicationId, payload);

      await Promise.all([updateUserFlowPromise, invalidateQueriesPromise, updateFaceVerifyPromise]);

      navigationContext.forward();
    } catch (err) {
      dispatch(
        snackbarSlice.actions.showError({
          message: err?.data?.message ?? t('candidate_recruiter:GENERAL.GENERIC.dcSdkCapturePhoto.dcValidationFailed'),
        }),
      );
    }
  };

  useMemo(() => {
    isSaveEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikRef?.current?.values, controls]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnMount={true}
        innerRef={formikRef}
        validateOnChange={true}
        initialValues={initialValues}
        validationSchema={dynamicValidationSchema}
        onSubmit={submitHandler}
      >
        {({ handleSubmit, isSubmitting, values, setErrors, setSubmitting, errors, setTouched, touched }) => (
          <form
            onSubmit={(e) => {
              handleSubmit(e);
            }}
            onChange={onFormChange}
            onClick={(e) => {
              onFormChange();
              if (!e || !(e?.target instanceof HTMLButtonElement)) {
                return;
              }

              const dataTestid = e?.target?.getAttribute('data-testid') ?? '';
              if (!['submitButton'].includes(dataTestid)) {
                return;
              }

              const addTouched = {} as Record<string, any>;
              Object.keys(errors).forEach((key: string) => {
                addTouched[key] = true;
              });
              if (!isEmpty(addTouched)) {
                setTouched({ ...touched, ...addTouched }, true);
                setClickCounter((prevValue: number) => prevValue + 1);
              }
            }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Box
              style={{
                display: 'grid',
                gap: '20px',
                paddingTop: '5px',
                paddingBottom: '20px',
                flexGrow: 1,
                alignContent: 'start',
              }}
            >
              {controls
                .filter((c) => isCandidateControl(c) && renderCondition(c, flowWrapper, values))
                .map((control: OOControlModel) => {
                  control.isMandatory = mandatoryCondition(control, flowWrapper, values) ?? control.isMandatory;
                  flowWrapper.getDisabledFieldForDE(control, flowWrapper);
                  return control;
                })
                .map((control: OOControlModel) => {
                  return (
                    <Grid key={control.name} item xs={12} style={{ minWidth: '100%' }}>
                      <OOFormControl
                        control={control}
                        otherFormControls={form.controls}
                        filterSubControls={(c: any) => isCandidateControl(c)}
                        setManualErrors={setManualErrors}
                        setSdkSuccess={setSdkSuccess}
                        setSdkError={setSdkError}
                        setSdkCapturedDocument={setSdkCapturedDocument}
                      />
                    </Grid>
                  );
                })}
            </Box>
            {form?.isLastPage && formHasCustomSubmitButtons ? (
              <Grid container item xs={12}>
                <Box py={2} flexGrow={1}>
                  {controls
                    .filter((c) => isCandidateSubmitOrDismissBtn(c))
                    .map((control: OOControlModel, index) => {
                      const prefix = getPrefix(control?.label);
                      let el: JSX.Element;
                      if (control.component === Component.SubmitButton) {
                        el = (
                          <Button
                            data-testid="submitButton"
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            {control.label
                              ? t(`${prefix}:${control.label}`)
                              : t('candidate_recruiter:GENERAL.GENERIC.submit')}
                          </Button>
                        );
                      } else if (control.component === Component.SubmitLink) {
                        el = (
                          <Button
                            data-testid="submitButton"
                            type="button"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                            onClick={() => {
                              window.open(control.link || '', '_blank');
                            }}
                          >
                            {t(`${prefix}:${control.label}`)}
                          </Button>
                        );
                      } else if (control.component === Component.DismissButton) {
                        el = (
                          <Button
                            data-testid="dismissButton"
                            variant="outlined"
                            color="primary"
                            disabled={isSubmitting}
                            onClick={() => dismissHandler(setErrors, setSubmitting)}
                          >
                            {control.label
                              ? t(`${prefix}:${control.label}`)
                              : t('candidate_recruiter:GENERAL.GENERIC.dismiss')}
                          </Button>
                        );
                      } else {
                        el = <OOFormControl control={control} setManualErrors={setManualErrors} />;
                      }
                      return (
                        <Box key={index} py={1}>
                          {el}
                        </Box>
                      );
                    })}
                </Box>
              </Grid>
            ) : !form?.hideSubmitButton && !form?.isLastPage ? (
              <BottomBox marginBottom="max(60px, env(safe-area-inset-bottom))">
                <Button
                  data-testid="submitButton"
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !saveEnabled}
                >
                  {t(
                    formIsStatic
                      ? 'candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.continue'
                      : 'candidate_recruiter:GENERAL.GENERIC.submit',
                  )}
                </Button>
                {controls
                  .filter((c) => isCandidateDismissBtn(c))
                  .map((control: OOControlModel, index) => {
                    return (
                      <Box py={1} key={index}>
                        <Button
                          data-testid="dismissButton"
                          variant="outlined"
                          color="primary"
                          disabled={isSubmitting}
                          onClick={() => dismissHandler(setErrors, setSubmitting)}
                        >
                          {control.label
                            ? t(`${getPrefix(control.label)}:${control.label}`)
                            : t('candidate_recruiter:GENERAL.GENERIC.dismiss')}
                        </Button>
                      </Box>
                    );
                  })}
              </BottomBox>
            ) : (
              <></>
            )}
          </form>
        )}
      </Formik>
      <SuccessPageDialog
        isVisible={sdkSuccess}
        setIsDataCheckerSuccess={setSdkSuccess}
        handleSubmit={handleDCDialogSubmit}
      />
      <ErrorPageDialog isVisible={sdkError} setIsDataCheckerError={setSdkError} />
    </>
  );
};
